import { IGroup, ModelType } from "./model.model";
import { WorkorderLineField } from "./workorder-line.model";

/**
 * Highest role of the logged-in employee, as returned in `role` by GET /authenticate/access.
 * The names come from the RBAC sheet (rbac_roles). An intern gets the role of the function they
 * are an intern for (stagiair monteur = mechanic); only owner + intern becomes `intern`.
 */
export enum AccessRole {
	Default = "default",
	Support = "support",
	Administrator = "administrator",
	Owner = "owner",
	ChefSales = "chef_sales",
	Purchaser = "purchaser",
	Sales = "sales",
	Receptionist = "receptionist",
	ChiefWorkshop = "chief_workshop",
	ManagerWarehouse = "manager_warehouse",
	MotInspector = "mot_inspector",
	Mechanic = "mechanic",
	Intern = "intern"
}

export enum Permission {
	/** Prices of vehicles and order lines. */
	ViewPrices = "view_prices",
	/** Prices in the vehicle history. */
	ViewHistoryPrices = "view_history_prices"
}

/** Either only the listed roles get the permission (allow), or everyone except the listed roles (deny). */
export type PermissionRule = { allow: readonly AccessRole[] } | { deny: readonly AccessRole[] };

/** Which roles have which permission. Change who may see what here. */
export const PERMISSION_RULES: Readonly<Record<Permission, PermissionRule>> = {
	// TEN-23054: only an employee whose highest role is mechanic may not see prices
	[Permission.ViewPrices]: { deny: [AccessRole.Mechanic] },
	// Mirrors Autoflex Cloud's history allow-list (TEN-22108)
	[Permission.ViewHistoryPrices]: {
		allow: [AccessRole.Owner, AccessRole.Administrator, AccessRole.ChefSales, AccessRole.Purchaser, AccessRole.ChiefWorkshop]
	}
};

export const VEHICLE_PRICE_GROUP = "Prijzen";

export interface IModelRestriction {
	/** Model group names (IGroup.name) to hide entirely. */
	groups?: readonly string[];
	/** Model field ids (IField.id) to hide. */
	fields?: readonly string[];
}

/** Which model groups/fields need a permission. Add a model or field here to hide it in every screen that uses the model. */
export const MODEL_RESTRICTIONS: Readonly<Partial<Record<ModelType, Partial<Record<Permission, IModelRestriction>>>>> = {
	[ModelType.Vehicle]: {
		[Permission.ViewPrices]: { groups: [VEHICLE_PRICE_GROUP] }
	},
	[ModelType.OrderLine]: {
		[Permission.ViewPrices]: {
			fields: [
				WorkorderLineField.Price,
				WorkorderLineField.PurchasePrice,
				WorkorderLineField.DiscountPercentage,
				WorkorderLineField.GrossTotalPrice,
				WorkorderLineField.VatTotal,
				WorkorderLineField.TotalIncludingVat
			]
		}
	}
};

export interface IHiddenModelItems {
	groups: ReadonlySet<string>;
	fields: ReadonlySet<string>;
}

/** An unknown role never gets a permission. */
export function roleHasPermission(role: string | null | undefined, permission: Permission): boolean {
	if (!role) return false;

	const rule = PERMISSION_RULES[permission];
	if ("allow" in rule) return rule.allow.includes(role as AccessRole);
	return !rule.deny.includes(role as AccessRole);
}

export function getHiddenModelItems(role: string | null | undefined, modelType: ModelType): IHiddenModelItems {
	const groups = new Set<string>();
	const fields = new Set<string>();

	for (const [permission, restriction] of Object.entries(MODEL_RESTRICTIONS[modelType] ?? {}) as [Permission, IModelRestriction][]) {
		if (roleHasPermission(role, permission)) continue;
		restriction.groups?.forEach((group) => groups.add(group));
		restriction.fields?.forEach((field) => fields.add(field));
	}

	return { groups, fields };
}

/** Groups without the hidden groups and fields; a group left without fields is dropped too. */
export function withoutHiddenModelItems<T extends IGroup>(groups: T[], hidden: IHiddenModelItems): T[] {
	return groups
		.filter((group) => !hidden.groups.has(group.name))
		.map((group) => ({ ...group, fields: group.fields.filter((field) => !hidden.fields.has(field.id)) }))
		.filter((group) => group.fields.length > 0);
}
